import { useTranslation } from 'react-i18next';
import { DrugSearchField } from './DrugSearchField';
import type { DrugSearchSelection } from './drugSearchSelection';
import { FormulationPicker } from './FormulationPicker';
import type { PrescribableMedication, SearchMedicationsQueryOptionsFactory } from './types';

export interface MedicationSearchStepProps {
  selection: DrugSearchSelection | null;
  onSelectionChange: (selection: DrugSearchSelection | null) => void;
  formulations: PrescribableMedication[];
  /** The selected formulation's `formulationKey` — RxNorm, or the NDC for a supply. */
  selectedCode: string;
  onFormulationChange: (code: string) => void;
  formulationsLoading: boolean;
  /** No dispensable formulation for the selection — manual dosage entry; show the notice (free-text
   * or no-strengths copy, by selection kind), hide the picker. */
  manualEntry: boolean;
  /** Pass-through to the app's `erx.searchMedications` query options. */
  searchMedications: SearchMedicationsQueryOptionsFactory;
  /** Keep the strength/form picker visible (disabled) before a drug is chosen, instead of hiding it
   * until then — so the field set is stable from the start. Off by default; the care-plan add flow
   * opts in (#8168). Manual/free-text entries still swap the picker for the notice. */
  alwaysShowFormulation?: boolean;
}

/** The two-step ScriptSure selection: a drug type-ahead, then (unless it's a manual entry) the
 * strength/form picker, with a notice when the medication is free text or has no formulation. */
export function MedicationSearchStep({
  selection,
  onSelectionChange,
  formulations,
  selectedCode,
  onFormulationChange,
  formulationsLoading,
  manualEntry,
  searchMedications,
  alwaysShowFormulation = false,
}: MedicationSearchStepProps): React.ReactNode {
  const { t } = useTranslation('records');
  const isDrug = selection?.kind === 'drug';
  return (
    <>
      <DrugSearchField value={selection} onChange={onSelectionChange} searchMedications={searchMedications} />

      {!manualEntry && (isDrug || alwaysShowFormulation) && (
        <FormulationPicker
          formulations={formulations}
          selectedCode={selectedCode}
          onFormulationChange={onFormulationChange}
          isLoading={formulationsLoading}
          disabled={!isDrug}
        />
      )}

      {manualEntry && (
        <p className="text-sm text-muted-foreground" data-testid="manual-entry-notice">
          {t(selection?.kind === 'freeText' ? 'freeTextManualNotice' : 'noStrengthsFoundManual')}
        </p>
      )}
    </>
  );
}
